/**
 * The app, below the session boundary (ORDR-30, wired to a real session in
 * ORDR-41).
 *
 * Which nodes the user holds and which door they came in by arrive from
 * `OrderingContextProvider`, which `main.tsx` mounts around this. By the time
 * this renders, that answer is resolved: loading, sign-in and "could not check
 * your access" are the provider's to render, never this component's.
 *
 * THE CUSTOMER ADDRESSES (ORDR-118). A Site orders; a brand with no Site
 * chooses a location first; neither is told to scan the code. Choosing is ONE
 * document load to `/order?brand=<b>&site=<s>&location=<l>` (plus `entry`),
 * not a `pushState`: the door and the provider are decided once at mount, so
 * the next screen must be a new mount.
 *
 * THE ORDER LINK (ORDR-226). A valid `?order=` on the customer surface is
 * decided FIRST, before the chooser and the no-site notice: the order id is
 * the whole key the status read (ORDR-162) and commerce's order read take, so
 * a diner who closed the tab gets back to the order with or without a brand,
 * Site or location in the address. With a Site the order still goes through
 * the customer wiring, exactly as before, which keeps the location's time
 * zone; without one the shell mounts the Status page alone, in a branch it
 * takes before the chooser's (`orderLink.test.tsx` holds that order). An
 * absent or malformed `?order=` changes nothing.
 */

import { useMemo, useState, type ReactElement } from "react";
import { PwaShell, type FlowFault, type ResolvedFlow } from "@pirouz-ca/ui-components";
import { createApiClient, type ApiClient } from "./api/client.js";
import { BrandedSurface } from "./brand/BrandedSurface.js";
import type { FinixCardDeps } from "@pirouz-ca/payments-core";
import { CARD_PLATFORM, ORDERING_CURRENCY, type FinixCardPlatform } from "./config/env.js";
import { createCardCaptureSlot, type CardCaptureSlot } from "./customer/cardTokeniser.js";
import { createCartSecretStore, type CartSecretStore } from "./customer/cartSecretStore.js";
import type { PaymentTokeniser } from "./customer/customerServices.js";
import { createDeviceStore, type DeviceStore } from "./kitchen/deviceStore.js";
import { brandFromSearch, locationFromSearch } from "./session/address.js";
import { orderFromSearch } from "./status/orderMemory.js";
import { useOrderingContext } from "./session/orderingContext.js";
import {
  OrderingShell,
  type CardWiring,
  type ChooserWiring,
  type CustomerWiring,
  type KitchenWiring,
  type OrderLinkWiring,
} from "./shell/OrderingShell.js";

export interface AppProps {
  flow?: ResolvedFlow;
  /** Injected for tests. Defaults to a client on `VITE_API_BASE_URL`. */
  client?: ApiClient;
  /** Injected for tests. Defaults to this browser's localStorage. */
  deviceStore?: DeviceStore;
  /** Injected for tests. Defaults to this tab's sessionStorage. */
  secrets?: CartSecretStore;
  /**
   * Injected for tests. Overrides the card tokeniser the shell builds from the
   * card slot, which it does only after commerce refuses pay in store.
   */
  tokeniser?: PaymentTokeniser;
  /**
   * The platform half of Finix hosted card fields (ORDR-55, ORDR-57): environment
   * and application id, defaulting to the VITE_FINIX_* build env. The merchant
   * id comes from the server for each location. `null` (or incomplete env)
   * means the diner is told payment is not connected and no SDK is loaded.
   */
  cardPlatform?: FinixCardPlatform | null;
  /** Injected for tests: replaces the hosted finix.js loader. */
  cardDeps?: FinixCardDeps;
  /** Injected for tests. Defaults to a document load with `window.location.assign`. */
  navigate?: (href: string) => void;
}

function documentLoad(href: string): void {
  window.location.assign(href);
}

function currentSearch(): string {
  return typeof window === "undefined" ? "" : window.location.search;
}

export function App({
  flow,
  client,
  deviceStore,
  secrets,
  tokeniser,
  cardPlatform = CARD_PLATFORM,
  cardDeps,
  navigate = documentLoad,
}: AppProps): ReactElement {
  const {
    surface,
    siteId,
    brandNodeId: hostBrandNodeId,
    nodes,
    siteIdByNode,
    nodeTypeByNode,
    ownUserId,
    activeFirmId,
  } = useOrderingContext();
  const [api] = useState<ApiClient>(() => client ?? createApiClient());
  const [devices] = useState<DeviceStore>(() => deviceStore ?? createDeviceStore());
  const [cartSecrets] = useState<CartSecretStore>(() => secrets ?? createCartSecretStore());
  const [cardSlot] = useState<CardCaptureSlot>(createCardCaptureSlot);
  // Read once, like the door: the address does not change under a mounted app.
  const [address] = useState(() => {
    const search = currentSearch();
    const entry = new URLSearchParams(search).get("entry");
    return {
      entry: entry === null || entry === "" ? null : entry,
      brandNodeId: brandFromSearch(search),
      locationNodeId: locationFromSearch(search),
      orderId: orderFromSearch(search),
    };
  });
  const card = useMemo<CardWiring | undefined>(
    () =>
      cardPlatform === null
        ? undefined
        : { platform: cardPlatform, slot: cardSlot, ...(cardDeps === undefined ? {} : { deps: cardDeps }) },
    [cardPlatform, cardSlot, cardDeps],
  );
  const kitchen = useMemo<KitchenWiring>(() => ({ client: api, deviceStore: devices }), [api, devices]);
  const customer = useMemo<CustomerWiring | undefined>(() => {
    if (siteId === null) {
      return undefined;
    }
    return {
      client: api,
      siteId,
      secrets: cartSecrets,
      currency: ORDERING_CURRENCY,
      ...(address.entry === null ? {} : { entry: address.entry }),
      ...(address.brandNodeId === null ? {} : { brandNodeId: address.brandNodeId }),
      ...(address.locationNodeId === null ? {} : { locationNodeId: address.locationNodeId }),
      ...(address.orderId === null ? {} : { orderId: address.orderId }),
      ...(tokeniser === undefined ? {} : { tokeniser }),
      ...(card === undefined ? {} : { card }),
      navigate,
    };
  }, [api, siteId, cartSecrets, tokeniser, card, address, navigate]);
  // ORDR-226. Only where no Site is named: with a Site, `customer` carries the
  // order exactly as it did before this existed.
  const orderLink = useMemo<OrderLinkWiring | undefined>(
    () =>
      surface !== "customer" || siteId !== null || address.orderId === null
        ? undefined
        : { client: api, secrets: cartSecrets, orderId: address.orderId },
    [surface, siteId, address, api, cartSecrets],
  );
  const chooser = useMemo<ChooserWiring | undefined>(() => {
    // `?brand=` when the address names one; otherwise the brand the host
    // resolved to on a brand domain (ORDR-164). Never both: the host door opens
    // only for an address with no `?brand=`.
    const brandNodeId = address.brandNodeId ?? hostBrandNodeId;
    if (surface !== "customer" || siteId !== null || brandNodeId === null) {
      return undefined;
    }
    return {
      brandNodeId,
      onChoose(chosenSite: string, chosenLocation: string): void {
        const query = new URLSearchParams({ brand: brandNodeId, site: chosenSite, location: chosenLocation });
        if (address.entry !== null) {
          query.set("entry", address.entry);
        }
        navigate(`/order?${query.toString()}`);
      },
    };
  }, [surface, siteId, address, hostBrandNodeId, navigate]);

  function reportFault(fault: FlowFault, refused: ResolvedFlow): void {
    // A server fault nobody is paged about is a server fault that stays broken.
    // `console.error` is the floor, not the destination.
    console.error("[ordering] refused a resolved flow", {
      fault,
      venueFormat: refused.venueFormat,
      channel: refused.channel,
      handoverMode: refused.handoverMode,
    });
  }

  if (surface === "customer" && siteId === null && chooser === undefined && orderLink === undefined) {
    // A diner cannot order from "somewhere". This is not the staff notice: it
    // says nothing about anybody's access. It is a customer screen, so it is
    // drawn in the organization's brand (ORDR-139).
    return (
      <BrandedSurface>
        <PwaShell>
          <p role="alert" data-testid="no-site" className="p-4 text-sm">
            Scan the code at your venue to start an order.
          </p>
        </PwaShell>
      </BrandedSurface>
    );
  }

  return (
    <OrderingShell
      nodes={nodes}
      surface={surface}
      {...(ownUserId === null ? {} : { ownUserId })}
      {...(flow === undefined ? {} : { flow })}
      onFlowFault={reportFault}
      siteIdByNode={siteIdByNode}
      nodeTypeByNode={nodeTypeByNode}
      businessChosen={activeFirmId !== null}
      kitchen={kitchen}
      {...(customer === undefined ? {} : { customer })}
      {...(chooser === undefined ? {} : { chooser })}
      {...(orderLink === undefined ? {} : { orderLink })}
      client={api}
    />
  );
}
